import { client } from 'src/enviroment/api/axios'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Mutation, Query } from '../index'
import { Tenant, UploadLogoResponse } from 'src/types/tenant'
import { useTenantStore } from 'src/enviroment/store/tenentStore'

export const tenantRouterKeys = {
  myTenants: 'tenant.myTenants',
  myTenantsWithProject: 'tenant.myTenantsWithProject',
  tenant: 'tenant.tenant',
} as const

export const tenantRouter = {
  myTenantsWithProject: {
    useQuery: (projectId: string, opts?: Query<Tenant[]>) => {
      return useQuery<Tenant[]>({
        queryKey: [tenantRouterKeys.myTenantsWithProject, projectId],
        queryFn: async () => {
          return (await client.get<Tenant[]>('/tenants:myself', { params: { projectId } })).data
        },
        ...opts,
      })
    },
  },
  myTenants: {
    useQuery: (opts?: Query<Tenant[]>) => {
      const { currentTenant, setCurrentTenant } = useTenantStore()

      return useQuery<Tenant[]>({
        queryKey: [tenantRouterKeys.myTenants],
        queryFn: async () => {
          return (await client.get<Tenant[]>('/tenants:myself')).data
        },
        onSuccess: (data) => {
          // Update current tenant if it exists in the response
          if (currentTenant?.id) {
            const updatedTenant = data.find((tenant) => tenant.id === currentTenant.id)
            if (updatedTenant && JSON.stringify(updatedTenant) !== JSON.stringify(currentTenant)) {
              setCurrentTenant(updatedTenant as unknown as typeof currentTenant)
            }
          }
        },
        ...opts,
      })
    },
  },
  byId: {
    useQuery: (id: string, opts?: Query<Tenant>) => {
      return useQuery<Tenant>({
        queryKey: [tenantRouterKeys.tenant, id],
        queryFn: async () => {
          return (await client.get<Tenant>(`/tenants/${id}`)).data
        },
        enabled: !!id,
        ...opts,
      })
    },
  },
  uploadLogo: {
    useMutation: (opts?: Mutation<File, UploadLogoResponse>) => {
      const queryClient = useQueryClient()
      const selectedTenantId = useTenantStore((state) => state.currentTenant?.id)

      return useMutation({
        mutationFn: async (file: File) => {
          if (!selectedTenantId) {
            throw new Error('No tenant selected')
          }

          const formData = new FormData()
          formData.append('file', file)

          return (
            await client.post<UploadLogoResponse>(`/tenants/${selectedTenantId}:logo`, formData, {
              headers: {
                'Content-Type': 'multipart/form-data',
              },
            })
          ).data
        },
        onSuccess: (data, ...args) => {
          // Update current tenant logo in store
          const currentTenant = useTenantStore.getState().currentTenant
          if (currentTenant) {
            useTenantStore.setState({
              currentTenant: {
                ...currentTenant,
                logoUrl: data.logoUrl,
              },
            })
          }

          queryClient.invalidateQueries({
            queryKey: [tenantRouterKeys.myTenants],
          })

          opts?.onSuccess?.(data, ...args)
        },
        ...opts,
      })
    },
  },
}
